/** Canonical v3 statuses currently known to the UI. Reference data remains authoritative. */
export const KNOWN_OPTION_STATUS = {
  IDENTIFIED: 'IDENTIFIED',
  DISCUSSED: 'DISCUSSED',
  QUALIFIED: 'QUALIFIED',
  SELECTED: 'SELECTED',
  DECLINED: 'DECLINED',
  ABANDONED: 'ABANDONED',
} as const;

export type KnownOptionStatusCode =
  (typeof KNOWN_OPTION_STATUS)[keyof typeof KNOWN_OPTION_STATUS];

/** Runtime system-code key. Unknown future keys render safely instead of breaking a list. */
export type CarePlanOptionStatusCode = string;
export type CarePlanOptionTypeCode = string;

export interface OptionReferenceValue {
  code: string;
  label: string;
  description?: string | null;
}

/** Every configured status is a candidate manual target. */
export function buildOptionStatusTransitions(
  statuses: readonly OptionReferenceValue[],
): Readonly<Record<string, readonly string[]>> {
  const codes = [
    ...new Set(
      statuses
        .map((status) => status.code.trim().toUpperCase())
        .filter(Boolean),
    ),
  ];
  return Object.fromEntries(
    codes.map((fromStatus) => [
      fromStatus,
      codes.filter((toStatus) => toStatus !== fromStatus),
    ]),
  );
}

/** Matrix entry. `id` is UI-only identity and is never sent to AIM. */
export interface CarePlanOptionTitle {
  id: string;
  title: string;
  optionTypeCode: CarePlanOptionTypeCode | null;
  requiresQualification: boolean;
  qualificationPrompt: string | null;
}

export interface OptionReferenceData {
  optionTypes: readonly OptionReferenceValue[];
  optionStatuses: readonly OptionReferenceValue[];
  optionTitles: readonly CarePlanOptionTitle[];
  allowedTransitions: Readonly<Record<string, readonly string[]>>;
}

export interface OptionReferenceCapabilities {
  allowCustomTitle: boolean;
  canRemove: boolean;
  canUpdateType: boolean;
}

export interface CarePlanOption {
  id: string;
  caseId: string;
  title: string;
  description: string | null;
  optionTypeCode: CarePlanOptionTypeCode;
  statusCode: CarePlanOptionStatusCode;
  sourceTypeCode: string | null;
  sourceId: number | null;
  createdAt?: string;
  updatedAt?: string;
}

/** Add-dialog value. Status defaults to IDENTIFIED when omitted. */
export interface CreateOptionValue {
  title: string;
  description?: string;
  optionTypeCode: CarePlanOptionTypeCode;
  statusCode?: CarePlanOptionStatusCode;
}

/** PATCH supports descriptive fields and status updates. */
export interface UpdateOptionValue {
  title?: string;
  description?: string;
  statusCode?: CarePlanOptionStatusCode;
}
